import {useEffect, useMemo, useRef, useState} from 'react';

import {throttle} from '@pexip/utils';

/**
 * Observes a HTMLElement and returns its bounding rect.
 * Updates when the element's size changes.
 *
 * If a ref to a HTMLElement is passed and ref.current is null it polls the ref to detect when
 * the HTMLElement value gets assigned to ref.current, this is useful in case the Component
 * using this hook does not rerender when ref.current changes.
 *
 * @param element - The HTMLElement, null, or a ref to an HTMLElement to observe
 * @param updateOnWindowResize - Whether to update the rect on window resize (default: false)
 * @param windowResizeThrottle - Throttle interval in ms for window resize updates (default: 0)
 * @returns The DOMRect of the element, or undefined if element is not available
 *
 * @example
 * ```ts
 * const ref = useRef<HTMLDivElement>(null);
 * const rect = useBoundingRect(ref);
 *
 * const [element, setElement] = useState<HTMLDivElement | null>(null);
 * const rect2 = useBoundingRect(element);
 *
 * // Update on window resize with 150ms throttle
 * const rect3 = useBoundingRect(ref, true, 150);
 * ```
 */
export const useBoundingRect = (
    element: HTMLElement | null | React.RefObject<HTMLElement | null>,
    updateOnWindowResize = false,
    windowResizeThrottle = 0,
): DOMRect | undefined => {
    const [rect, setRect] = useState<DOMRect | undefined>(undefined);
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined,
    );
    const resolvedElement =
        element instanceof HTMLElement
            ? element
            : element && 'current' in element
              ? element.current
              : null;

    // Polling effect for detecting when a ref.current changes from null to an element
    useEffect(() => {
        const isRef = element && 'current' in element;
        if (!isRef) {
            return;
        }

        // Only poll if current is null
        if (element.current !== null) {
            return;
        }

        let elapsedTime = 0;
        /*
         * We poll for maximum of 3 seconds, ref.current should definitely have the HTMLElement
         * assigned before 3 seconds have passed, if it is not we stop polling.
         */
        const maxTime = 3000;
        const pollInterval = 100;

        const poll = () => {
            elapsedTime += pollInterval;

            if (element.current !== null) {
                setRect(element.current.getBoundingClientRect());
            } else if (elapsedTime < maxTime) {
                // If we've not exceeded the max time, continue polling.
                timeoutIdRef.current = setTimeout(poll, pollInterval);
            }
        };

        timeoutIdRef.current = setTimeout(poll, pollInterval);

        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [element]);

    // Main observer effect
    useEffect(() => {
        if (!resolvedElement) {
            setRect(undefined);
            return;
        }

        setRect(resolvedElement.getBoundingClientRect());

        const resizeObserver = new ResizeObserver(() => {
            setRect(resolvedElement.getBoundingClientRect());
        });

        resizeObserver.observe(resolvedElement);

        return () => {
            resizeObserver.unobserve(resolvedElement);
            resizeObserver.disconnect();
        };
    }, [resolvedElement]);

    // Window resize effect
    const throttledUpdate = useMemo(
        () =>
            throttle(() => {
                if (resolvedElement) {
                    setRect(resolvedElement.getBoundingClientRect());
                }
            }, windowResizeThrottle),
        [resolvedElement, windowResizeThrottle],
    );

    useEffect(() => {
        if (!updateOnWindowResize || !resolvedElement) {
            return;
        }

        window.addEventListener('resize', throttledUpdate);
        return () => {
            throttledUpdate.cancel();
            window.removeEventListener('resize', throttledUpdate);
        };
    }, [updateOnWindowResize, resolvedElement, throttledUpdate]);

    useEffect(() => {
        if (!resolvedElement || !screen.orientation) {
            return;
        }

        const update = () => {
            if (resolvedElement) {
                setRect(resolvedElement.getBoundingClientRect());
            }
        };

        screen.orientation.addEventListener('change', update);
        return () => {
            screen.orientation.removeEventListener('change', update);
        };
    }, [resolvedElement]);

    return rect;
};
